import styled, { createGlobalStyle, css } from 'styled-components';

export const fonts = {
  body: `'Schibsted Grotesk', ui-sans-serif, -apple-system, 'Segoe UI', sans-serif`,
  // SF Mono on Apple platforms — the font Xcode actually uses.
  mono: `ui-monospace, 'SF Mono', 'IBM Plex Mono', Menlo, Consolas, monospace`,
};

// ~1.25 modular scale; 17px body matches iOS.
export const text = {
  xs: '0.8125rem',
  sm: '0.9375rem',
  base: '1.0625rem',
  lg: '1.3125rem',
  xl: '1.625rem',
  xxl: 'clamp(2rem, 4.2vw, 2.75rem)',
  display: 'clamp(2.75rem, 7.5vw, 5rem)',
};

export const radius = {
  sm: '6px',
  md: '10px',
  lg: '14px',
};

// Strong ease-out for anything entering or responding to input; drawer curve for iOS-like movement.
export const ease = {
  out: [0.23, 1, 0.32, 1] as const,
  inOut: [0.77, 0, 0.175, 1] as const,
  drawer: [0.32, 0.72, 0, 1] as const,
};

export const easeCss = {
  out: 'cubic-bezier(0.23, 1, 0.32, 1)',
  inOut: 'cubic-bezier(0.77, 0, 0.175, 1)',
  drawer: 'cubic-bezier(0.32, 0.72, 0, 1)',
};

export const hover = '@media (hover: hover) and (pointer: fine)';

export const alpha = (color: string, percent: number) => `color-mix(in oklch, ${color} ${percent}%, transparent)`;

export const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    scroll-padding-top: 72px;
    -webkit-text-size-adjust: 100%;
  }

  body {
    font-family: ${fonts.body};
    font-size: ${text.base};
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
    transition: background-color 0.2s ease, color 0.2s ease;
    overflow-x: hidden;
  }

  #root {
    min-height: 100vh;
  }

  ::selection {
    background: ${props => alpha(props.theme.colors.primary, 28)};
  }

  a {
    color: inherit;
  }

  button {
    font: inherit;
    color: inherit;
  }

  :focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }

  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.borderStrong};
    border: 3px solid ${props => props.theme.colors.background};
    border-radius: 10px;
  }

  @media (prefers-reduced-motion: reduce) {
    html {
      scroll-behavior: auto;
    }

    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

export const Container = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 32px;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

export const SectionShell = styled.section`
  position: relative;
  padding: 128px 0;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 88px 0;
  }
`;

// Shared press/hover behaviour for every button-like control.
export const pressable = css`
  transition:
    background-color 150ms ease,
    border-color 150ms ease,
    color 150ms ease,
    transform 160ms ${easeCss.out};

  &:active:not(:disabled) {
    transform: scale(0.97);
  }
`;

export const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 18px;
  border-radius: ${radius.md};
  font-size: ${text.sm};
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  ${pressable}
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  border: 1px solid transparent;
  background: ${props => props.theme.colors.text};
  color: ${props => props.theme.colors.background};

  ${hover} {
    &:hover {
      background: ${props => alpha(props.theme.colors.text, 86)};
    }
  }
`;

export const SecondaryButton = styled.button`
  ${buttonBase}
  border: 1px solid ${props => props.theme.colors.borderStrong};
  background: transparent;
  color: ${props => props.theme.colors.text};

  ${hover} {
    &:hover {
      background: ${props => props.theme.colors.backgroundAlt};
    }
  }
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: ${radius.sm};
  font-size: ${text.xs};
  font-weight: 500;
  color: ${props => props.theme.colors.textSecondary};
  background: ${props => props.theme.colors.backgroundAlt};
  white-space: nowrap;
`;
